import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  CursorArrowRaysIcon,
  DocumentDuplicateIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import DiagramCanvas, { controlFromHandle, lineMidHandle } from './DiagramCanvas';
import { downloadDiagramPng } from './diagramExport';
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  Diagram,
  DiagramElement,
  EquipmentKind,
  INK_COLORS,
  LineElement,
  LineStyle,
  PitchType,
  TEAM_COLORS,
  TeamColor,
  ZoneElement,
  emptyDiagram,
  newId,
} from './types';

type Point = { x: number; y: number };
type Tool = 'select' | 'player' | 'equipment' | 'line' | 'zone' | 'text';
type Corner = 'nw' | 'ne' | 'sw' | 'se';

type Drag =
  | { kind: 'move'; id: string; start: Point; original: DiagramElement }
  | { kind: 'line-end'; id: string; end: 1 | 2 }
  | { kind: 'line-mid'; id: string }
  | { kind: 'zone-resize'; id: string; corner: Corner; original: ZoneElement }
  | { kind: 'create-line'; id: string }
  | { kind: 'create-zone'; id: string; origin: Point };

interface History {
  past: Diagram[];
  present: Diagram;
  future: Diagram[];
}

const MAX_HISTORY = 100;

const EQUIPMENT_OPTIONS: { kind: EquipmentKind; label: string; color: string }[] = [
  { kind: 'ball', label: 'Labda', color: '#ffffff' },
  { kind: 'cone', label: 'Bója', color: '#f97316' },
  { kind: 'disc', label: 'Korong', color: '#facc15' },
  { kind: 'pole', label: 'Bot', color: '#f59e0b' },
  { kind: 'minigoal', label: 'Kiskapu', color: '#ffffff' },
  { kind: 'goal', label: 'Kapu', color: '#ffffff' },
];

const EQUIPMENT_COLORS = ['#f97316', '#facc15', '#dc2626', '#2563eb', '#ffffff'];

const LINE_OPTIONS: { style: LineStyle; label: string; hint: string }[] = [
  { style: 'pass', label: 'Passz', hint: 'folytonos nyíl' },
  { style: 'shot', label: 'Lövés', hint: 'dupla szárú nyíl' },
  { style: 'run', label: 'Futás', hint: 'szaggatott nyíl' },
  { style: 'dribble', label: 'Labdavezetés', hint: 'hullámos nyíl' },
  { style: 'line', label: 'Vonal', hint: 'nyíl nélkül' },
];

const ZONE_COLORS = ['#facc15', '#ffffff', '#dc2626', '#2563eb', '#111827'];

const PITCH_OPTIONS: { value: PitchType; label: string }[] = [
  { value: 'full', label: 'Teljes pálya' },
  { value: 'half', label: 'Fél pálya' },
  { value: 'blank', label: 'Üres terület' },
];

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const isTyping = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
};

const translate = (el: DiagramElement, dx: number, dy: number): DiagramElement => {
  if (el.type === 'line') {
    return {
      ...el,
      x1: el.x1 + dx,
      y1: el.y1 + dy,
      x2: el.x2 + dx,
      y2: el.y2 + dy,
      ...(el.cx !== undefined && el.cy !== undefined ? { cx: el.cx + dx, cy: el.cy + dy } : {}),
    };
  }
  return { ...el, x: el.x + dx, y: el.y + dy } as DiagramElement;
};

const sameDiagram = (a: Diagram, b: Diagram) => a === b || JSON.stringify(a) === JSON.stringify(b);

// ---------------------------------------------------------------------------
// Kis ikonok az eszköztárhoz
// ---------------------------------------------------------------------------

const ToolIcon: React.FC<{ tool: Tool }> = ({ tool }) => {
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', 'aria-hidden': true } as const;
  switch (tool) {
    case 'player':
      return (
        <svg {...common}>
          <circle cx={12} cy={12} r={8} fill="#dc2626" stroke="currentColor" strokeWidth={1.5} />
          <text x={12} y={12} textAnchor="middle" dominantBaseline="central" fontSize={9} fontWeight={700} fill="#fff">
            7
          </text>
        </svg>
      );
    case 'equipment':
      return (
        <svg {...common}>
          <path d="M12 4 L19 18 L5 18 Z" fill="#f97316" stroke="currentColor" strokeWidth={1.2} />
        </svg>
      );
    case 'line':
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
          <path d="M4 18 Q 10 4 19 7" />
          <path d="M15 4.5 L19.5 7 L16 10.5" />
        </svg>
      );
    case 'zone':
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeWidth={1.8} strokeDasharray="3 2">
          <rect x={4} y={6} width={16} height={12} rx={2} />
        </svg>
      );
    case 'text':
      return (
        <svg {...common}>
          <text x={12} y={13} textAnchor="middle" dominantBaseline="central" fontSize={16} fontWeight={700} fill="currentColor">
            T
          </text>
        </svg>
      );
    default:
      return <CursorArrowRaysIcon className="h-[22px] w-[22px]" />;
  }
};

const TOOLS: { tool: Tool; label: string }[] = [
  { tool: 'select', label: 'Kijelölés' },
  { tool: 'player', label: 'Játékos' },
  { tool: 'equipment', label: 'Eszköz' },
  { tool: 'line', label: 'Nyíl' },
  { tool: 'zone', label: 'Zóna' },
  { tool: 'text', label: 'Szöveg' },
];

const Swatch: React.FC<{ color: string; active: boolean; onClick: () => void; label: string }> = ({
  color,
  active,
  onClick,
  label,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    aria-pressed={active}
    title={label}
    className={`h-8 w-8 rounded-full border-2 transition ${
      active ? 'border-primary-600 ring-2 ring-primary-300' : 'border-gray-300 hover:border-gray-500'
    }`}
    style={{ backgroundColor: color }}
  />
);

const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode; title?: string }> = ({
  active,
  onClick,
  children,
  title,
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-pressed={active}
    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
      active
        ? 'bg-primary-600 border-primary-600 text-white'
        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
    }`}
  >
    {children}
  </button>
);

// ---------------------------------------------------------------------------
// Szerkesztő
// ---------------------------------------------------------------------------

interface DiagramEditorProps {
  initial: Diagram | null;
  /** A letöltött kép neve és a fejléc szövege. */
  title?: string;
  /** Mentés: null, ha az ábra üres (a rajz törlődik). */
  onSave: (diagram: Diagram | null) => void;
  onCancel: () => void;
}

/**
 * Taktikai rajzszerkesztő edzésgyakorlatokhoz: pálya, játékosok, eszközök,
 * passz-/futás-/labdavezetés-nyilak, zónák és szövegek. Egérrel és érintéssel
 * is kezelhető; teljes képernyőn nyílik meg.
 */
const DiagramEditor: React.FC<DiagramEditorProps> = ({ initial, title, onSave, onCancel }) => {
  const [hist, setHist] = useState<History>(() => ({
    past: [],
    present: initial ?? emptyDiagram(),
    future: [],
  }));
  const diagram = hist.present;

  const [tool, setTool] = useState<Tool>(initial && initial.elements.length > 0 ? 'select' : 'player');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [playerColor, setPlayerColor] = useState<TeamColor>('red');
  const [equipmentKind, setEquipmentKind] = useState<EquipmentKind>('cone');
  const [lineStyle, setLineStyle] = useState<LineStyle>('pass');
  const [lineColor, setLineColor] = useState<string>('#ffffff');
  const [zoneShape, setZoneShape] = useState<'rect' | 'ellipse'>('rect');
  const [exporting, setExporting] = useState(false);

  const svgRef = useRef<SVGSVGElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const snapshotRef = useRef<Diagram | null>(null);

  const selected = diagram.elements.find((el) => el.id === selectedId) ?? null;

  // --- Előzmények ----------------------------------------------------------

  /** Azonnali, visszavonható módosítás. */
  const change = useCallback((fn: (d: Diagram) => Diagram) => {
    setHist((h) => {
      const next = fn(h.present);
      if (sameDiagram(next, h.present)) return h;
      return { past: [...h.past, h.present].slice(-MAX_HISTORY), present: next, future: [] };
    });
  }, []);

  /** Húzás közbeni frissítés - az előzménybe csak a húzás végén kerül. */
  const preview = useCallback((fn: (d: Diagram) => Diagram) => {
    setHist((h) => ({ ...h, present: fn(h.present) }));
  }, []);

  const commitFrom = useCallback((snapshot: Diagram | null) => {
    if (!snapshot) return;
    setHist((h) =>
      sameDiagram(snapshot, h.present)
        ? h
        : { past: [...h.past, snapshot].slice(-MAX_HISTORY), present: h.present, future: [] }
    );
  }, []);

  const undo = useCallback(() => {
    setHist((h) =>
      h.past.length === 0
        ? h
        : { past: h.past.slice(0, -1), present: h.past[h.past.length - 1], future: [h.present, ...h.future] }
    );
  }, []);

  const redo = useCallback(() => {
    setHist((h) =>
      h.future.length === 0 ? h : { past: [...h.past, h.present], present: h.future[0], future: h.future.slice(1) }
    );
  }, []);

  const updateElement = (d: Diagram, id: string, fn: (el: DiagramElement) => DiagramElement): Diagram => ({
    ...d,
    elements: d.elements.map((el) => (el.id === id ? fn(el) : el)),
  });

  const patchSelected = (patch: Partial<DiagramElement>) => {
    if (!selectedId) return;
    change((d) => updateElement(d, selectedId, (el) => ({ ...el, ...patch } as DiagramElement)));
  };

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    change((d) => ({ ...d, elements: d.elements.filter((el) => el.id !== selectedId) }));
    setSelectedId(null);
  }, [change, selectedId]);

  const duplicateSelected = useCallback(() => {
    if (!selected) return;
    const copy = { ...translate(selected, 24, 24), id: newId() } as DiagramElement;
    change((d) => ({ ...d, elements: [...d.elements, copy] }));
    setSelectedId(copy.id);
  }, [change, selected]);

  // --- Billentyűk ----------------------------------------------------------

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      const mod = e.metaKey || e.ctrlKey;

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault();
        deleteSelected();
      } else if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
      } else if (mod && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        duplicateSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteSelected, duplicateSelected, redo, undo, selectedId]);

  // A szerkesztő tartsa a fókuszt, hogy a billentyűk (és az Esc) ide érkezzenek,
  // ne a mögötte lévő esemény-ablakhoz
  useEffect(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);

  const handleRootKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    // Az Esc ne zárja be a szülő párbeszédablakot, csak a kijelölést szüntesse meg
    e.stopPropagation();
    e.preventDefault();
    if (isTyping(e.target)) {
      (e.target as HTMLElement).blur();
      rootRef.current?.focus({ preventScroll: true });
      return;
    }
    setSelectedId(null);
    setTool('select');
  };

  // --- Mutató (egér / érintés) ---------------------------------------------

  const toSvg = (e: React.PointerEvent): Point => {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: clamp(p.x, 0, CANVAS_WIDTH), y: clamp(p.y, 0, CANVAS_HEIGHT) };
  };

  const capture = (e: React.PointerEvent) => {
    try {
      svgRef.current?.setPointerCapture(e.pointerId);
    } catch {
      // Néhány böngésző nem engedi - a húzás ettől még működik a vásznon belül
    }
  };

  const startDrag = (e: React.PointerEvent, drag: Drag) => {
    snapshotRef.current = diagram;
    dragRef.current = drag;
    capture(e);
  };

  const nextPlayerLabel = (color: TeamColor) =>
    String(diagram.elements.filter((el) => el.type === 'player' && el.color === color).length + 1);

  const handleCanvasPointerDown = (e: React.PointerEvent) => {
    if (e.button !== undefined && e.button > 0) return;
    rootRef.current?.focus({ preventScroll: true });
    const p = toSvg(e);

    switch (tool) {
      case 'select':
        setSelectedId(null);
        return;
      case 'player': {
        const id = newId();
        change((d) => ({
          ...d,
          elements: [...d.elements, { id, type: 'player', x: p.x, y: p.y, color: playerColor, label: nextPlayerLabel(playerColor) }],
        }));
        setSelectedId(id);
        return;
      }
      case 'equipment': {
        const id = newId();
        const option = EQUIPMENT_OPTIONS.find((o) => o.kind === equipmentKind)!;
        change((d) => ({
          ...d,
          elements: [...d.elements, { id, type: 'equipment', kind: equipmentKind, x: p.x, y: p.y, rotation: 0, color: option.color }],
        }));
        setSelectedId(id);
        return;
      }
      case 'text': {
        const id = newId();
        change((d) => ({
          ...d,
          elements: [...d.elements, { id, type: 'text', x: p.x, y: p.y, text: 'Szöveg', color: '#ffffff', size: 28 }],
        }));
        setSelectedId(id);
        setTool('select');
        return;
      }
      case 'line': {
        const id = newId();
        startDrag(e, { kind: 'create-line', id });
        preview((d) => ({
          ...d,
          elements: [...d.elements, { id, type: 'line', x1: p.x, y1: p.y, x2: p.x, y2: p.y, style: lineStyle, color: lineColor }],
        }));
        setSelectedId(id);
        return;
      }
      case 'zone': {
        const id = newId();
        startDrag(e, { kind: 'create-zone', id, origin: p });
        preview((d) => ({
          ...d,
          elements: [...d.elements, { id, type: 'zone', shape: zoneShape, x: p.x, y: p.y, w: 0, h: 0, color: '#facc15' }],
        }));
        setSelectedId(id);
        return;
      }
    }
  };

  const handleElementPointerDown = (e: React.PointerEvent, id: string) => {
    // Rajzoló eszközzel egy elemre kattintva is rajzolunk (pl. nyíl egy játékostól)
    if (tool !== 'select') {
      handleCanvasPointerDown(e);
      return;
    }
    e.stopPropagation();
    rootRef.current?.focus({ preventScroll: true });
    const el = diagram.elements.find((x) => x.id === id);
    if (!el) return;
    setSelectedId(id);
    startDrag(e, { kind: 'move', id, start: toSvg(e), original: el });
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const p = toSvg(e);

    switch (drag.kind) {
      case 'move': {
        const dx = p.x - drag.start.x;
        const dy = p.y - drag.start.y;
        preview((d) => updateElement(d, drag.id, () => translate(drag.original, dx, dy)));
        return;
      }
      case 'line-end':
      case 'create-line':
        preview((d) =>
          updateElement(d, drag.id, (el) =>
            drag.kind === 'line-end' && drag.end === 1 ? { ...el, x1: p.x, y1: p.y } : { ...el, x2: p.x, y2: p.y }
          )
        );
        return;
      case 'line-mid':
        preview((d) =>
          updateElement(d, drag.id, (el) => {
            const c = controlFromHandle(el as LineElement, p);
            return { ...el, cx: c.x, cy: c.y };
          })
        );
        return;
      case 'create-zone':
        preview((d) =>
          updateElement(d, drag.id, (el) => ({
            ...el,
            x: Math.min(drag.origin.x, p.x),
            y: Math.min(drag.origin.y, p.y),
            w: Math.abs(p.x - drag.origin.x),
            h: Math.abs(p.y - drag.origin.y),
          }))
        );
        return;
      case 'zone-resize': {
        const o = drag.original;
        const left = drag.corner.includes('w') ? p.x : o.x;
        const right = drag.corner.includes('e') ? p.x : o.x + o.w;
        const top = drag.corner.includes('n') ? p.y : o.y;
        const bottom = drag.corner.includes('s') ? p.y : o.y + o.h;
        preview((d) =>
          updateElement(d, drag.id, (el) => ({
            ...el,
            x: Math.min(left, right),
            y: Math.min(top, bottom),
            w: Math.abs(right - left),
            h: Math.abs(bottom - top),
          }))
        );
        return;
      }
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;

    // Az elengedés helye is számít: gyors húzásnál az utolsó mozgás-esemény
    // még nem a végpontnál volt
    handlePointerMove(e);
    const p = toSvg(e);

    dragRef.current = null;
    const snapshot = snapshotRef.current;
    snapshotRef.current = null;

    const created = diagram.elements.find((el) => el.id === drag.id);
    const tooSmall =
      (drag.kind === 'create-line' && created?.type === 'line' && Math.hypot(p.x - created.x1, p.y - created.y1) < 14) ||
      (drag.kind === 'create-zone' && (Math.abs(p.x - drag.origin.x) < 12 || Math.abs(p.y - drag.origin.y) < 12));

    if (tooSmall) {
      // Csak egy kattintás volt, nem húzás: eldobjuk a félkész elemet
      preview((d) => ({ ...d, elements: d.elements.filter((el) => el.id !== drag.id) }));
      setSelectedId(null);
      return;
    }

    commitFrom(snapshot);
  };

  const startHandleDrag = (e: React.PointerEvent, drag: Drag) => {
    e.stopPropagation();
    startDrag(e, drag);
  };

  // --- Kijelölés kerete és fogópontjai --------------------------------------

  const handleCircle = (x: number, y: number, onDown: (e: React.PointerEvent) => void, key: string, accent = false) => (
    <circle
      key={key}
      cx={x}
      cy={y}
      r={10}
      fill={accent ? '#fde047' : '#ffffff'}
      stroke="#1d4ed8"
      strokeWidth={2.5}
      style={{ cursor: 'grab' }}
      onPointerDown={onDown}
    />
  );

  const renderOverlay = () => {
    if (!selected) return null;
    const box = { stroke: '#93c5fd', strokeWidth: 2, strokeDasharray: '6 4', fill: 'none' } as const;

    switch (selected.type) {
      case 'player':
        return <circle cx={selected.x} cy={selected.y} r={23} {...box} />;
      case 'equipment': {
        const r = selected.kind === 'goal' ? 46 : selected.kind === 'minigoal' ? 28 : 22;
        return <circle cx={selected.x} cy={selected.y} r={r} {...box} />;
      }
      case 'text': {
        const w = Math.max(40, selected.text.length * selected.size * 0.58);
        return <rect x={selected.x - w / 2 - 8} y={selected.y - selected.size / 2 - 6} width={w + 16} height={selected.size + 12} rx={6} {...box} />;
      }
      case 'line': {
        const mid = lineMidHandle(selected);
        return (
          <>
            {handleCircle(selected.x1, selected.y1, (e) => startHandleDrag(e, { kind: 'line-end', id: selected.id, end: 1 }), 'a')}
            {handleCircle(selected.x2, selected.y2, (e) => startHandleDrag(e, { kind: 'line-end', id: selected.id, end: 2 }), 'b')}
            {handleCircle(mid.x, mid.y, (e) => startHandleDrag(e, { kind: 'line-mid', id: selected.id }), 'm', true)}
          </>
        );
      }
      case 'zone': {
        const corners: { c: Corner; x: number; y: number }[] = [
          { c: 'nw', x: selected.x, y: selected.y },
          { c: 'ne', x: selected.x + selected.w, y: selected.y },
          { c: 'sw', x: selected.x, y: selected.y + selected.h },
          { c: 'se', x: selected.x + selected.w, y: selected.y + selected.h },
        ];
        return (
          <>
            <rect x={selected.x} y={selected.y} width={selected.w} height={selected.h} {...box} />
            {corners.map(({ c, x, y }) =>
              handleCircle(x, y, (e) => startHandleDrag(e, { kind: 'zone-resize', id: selected.id, corner: c, original: selected }), c)
            )}
          </>
        );
      }
    }
  };

  // --- Műveletek -----------------------------------------------------------

  const hasChanges = hist.past.length > 0;

  const handleCancel = () => {
    if (hasChanges && !window.confirm('Elveted a rajzon végzett módosításokat?')) return;
    onCancel();
  };

  const handleSave = () => {
    onSave(diagram.elements.length > 0 ? diagram : null);
  };

  const handleExport = async () => {
    if (!svgRef.current) return;
    setExporting(true);
    try {
      const fileName = (title || 'edzes-abra')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9-]+/g, '-')
        .toLowerCase();
      await downloadDiagramPng(svgRef.current, fileName || 'edzes-abra');
    } catch (err: any) {
      window.alert(err.message || 'A kép mentése nem sikerült.');
    } finally {
      setExporting(false);
    }
  };

  const clearAll = () => {
    if (diagram.elements.length === 0) return;
    if (!window.confirm('Törlöd az összes elemet a rajzról?')) return;
    change((d) => ({ ...d, elements: [] }));
    setSelectedId(null);
  };

  // --- Tulajdonságpanel -----------------------------------------------------

  const toolHint: Record<Tool, string> = {
    select: 'Kattints egy elemre a kijelöléshez, húzd a mozgatáshoz.',
    player: 'Kattints a pályára a játékos lerakásához. A mez száma automatikusan nő.',
    equipment: 'Válaszd ki az eszközt, majd kattints a pályára.',
    line: 'Húzd az egeret (vagy az ujjad) a nyíl kezdőpontjától a végéig. A sárga fogóponttal ívelheted.',
    zone: 'Húzással rajzolj egy területet (pl. játéktér, zóna).',
    text: 'Kattints a pályára, ahová a feliratot szeretnéd.',
  };

  const renderToolOptions = () => {
    switch (tool) {
      case 'player':
        return (
          <div className="flex flex-wrap items-center gap-2">
            {(Object.keys(TEAM_COLORS) as TeamColor[]).map((c) => (
              <Swatch key={c} color={TEAM_COLORS[c].fill} label={TEAM_COLORS[c].label} active={playerColor === c} onClick={() => setPlayerColor(c)} />
            ))}
          </div>
        );
      case 'equipment':
        return (
          <div className="flex flex-wrap gap-2">
            {EQUIPMENT_OPTIONS.map((o) => (
              <Chip key={o.kind} active={equipmentKind === o.kind} onClick={() => setEquipmentKind(o.kind)}>
                {o.label}
              </Chip>
            ))}
          </div>
        );
      case 'line':
        return (
          <div className="flex flex-wrap items-center gap-2">
            {LINE_OPTIONS.map((o) => (
              <Chip key={o.style} active={lineStyle === o.style} onClick={() => setLineStyle(o.style)} title={o.hint}>
                {o.label}
              </Chip>
            ))}
            <span className="mx-1 h-6 w-px bg-gray-300" />
            {INK_COLORS.map((c) => (
              <Swatch key={c} color={c} label="Vonal színe" active={lineColor === c} onClick={() => setLineColor(c)} />
            ))}
          </div>
        );
      case 'zone':
        return (
          <div className="flex flex-wrap gap-2">
            <Chip active={zoneShape === 'rect'} onClick={() => setZoneShape('rect')}>Téglalap</Chip>
            <Chip active={zoneShape === 'ellipse'} onClick={() => setZoneShape('ellipse')}>Ellipszis</Chip>
          </div>
        );
      default:
        return null;
    }
  };

  const renderProperties = () => {
    if (!selected) {
      return (
        <div className="space-y-3 text-sm text-gray-600">
          <p>{toolHint[tool]}</p>
          <ul className="text-xs text-gray-500 space-y-1">
            <li><kbd className="font-mono">Delete</kbd> – kijelölt elem törlése</li>
            <li><kbd className="font-mono">Ctrl/⌘ + Z</kbd> – visszavonás</li>
            <li><kbd className="font-mono">Ctrl/⌘ + D</kbd> – duplikálás</li>
            <li><kbd className="font-mono">Esc</kbd> – kijelölés megszüntetése</li>
          </ul>
        </div>
      );
    }

    const label = 'block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1.5';

    return (
      <div className="space-y-4">
        {selected.type === 'player' && (
          <>
            <div>
              <span className={label}>Csapat</span>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(TEAM_COLORS) as TeamColor[]).map((c) => (
                  <Swatch key={c} color={TEAM_COLORS[c].fill} label={TEAM_COLORS[c].label} active={selected.color === c} onClick={() => patchSelected({ color: c })} />
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="diagram-player-label" className={label}>Felirat (mezszám / betű)</label>
              <input
                id="diagram-player-label"
                type="text"
                maxLength={3}
                value={selected.label}
                onFocus={() => (snapshotRef.current = diagram)}
                onChange={(e) => preview((d) => updateElement(d, selected.id, (el) => ({ ...el, label: e.target.value })))}
                onBlur={() => commitFrom(snapshotRef.current)}
                className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </>
        )}

        {selected.type === 'equipment' && (
          <>
            <p className="text-sm font-medium text-gray-900">
              {EQUIPMENT_OPTIONS.find((o) => o.kind === selected.kind)?.label}
            </p>
            {['cone', 'disc', 'pole'].includes(selected.kind) && (
              <div>
                <span className={label}>Szín</span>
                <div className="flex flex-wrap gap-2">
                  {EQUIPMENT_COLORS.map((c) => (
                    <Swatch key={c} color={c} label="Szín" active={selected.color === c} onClick={() => patchSelected({ color: c })} />
                  ))}
                </div>
              </div>
            )}
            {(selected.kind === 'goal' || selected.kind === 'minigoal') && (
              <div>
                <label htmlFor="diagram-rotation" className={label}>Elforgatás: {selected.rotation}°</label>
                <input
                  id="diagram-rotation"
                  type="range"
                  min={0}
                  max={345}
                  step={15}
                  value={selected.rotation}
                  onPointerDown={() => (snapshotRef.current = diagram)}
                  onChange={(e) => preview((d) => updateElement(d, selected.id, (el) => ({ ...el, rotation: Number(e.target.value) })))}
                  onPointerUp={() => commitFrom(snapshotRef.current)}
                  className="w-full"
                />
              </div>
            )}
          </>
        )}

        {selected.type === 'line' && (
          <>
            <div>
              <span className={label}>Típus</span>
              <div className="flex flex-wrap gap-2">
                {LINE_OPTIONS.map((o) => (
                  <Chip key={o.style} active={selected.style === o.style} onClick={() => patchSelected({ style: o.style })} title={o.hint}>
                    {o.label}
                  </Chip>
                ))}
              </div>
            </div>
            <div>
              <span className={label}>Szín</span>
              <div className="flex flex-wrap gap-2">
                {INK_COLORS.map((c) => (
                  <Swatch key={c} color={c} label="Szín" active={selected.color === c} onClick={() => patchSelected({ color: c })} />
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {selected.cx !== undefined && (
                <button
                  type="button"
                  onClick={() => change((d) => updateElement(d, selected.id, (el) => ({ ...el, cx: undefined, cy: undefined })))}
                  className="px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Kiegyenesítés
                </button>
              )}
              <button
                type="button"
                onClick={() =>
                  patchSelected({ x1: selected.x2, y1: selected.y2, x2: selected.x1, y2: selected.y1 } as Partial<LineElement>)
                }
                className="px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                Irány megfordítása
              </button>
            </div>
          </>
        )}

        {selected.type === 'zone' && (
          <>
            <div>
              <span className={label}>Forma</span>
              <div className="flex gap-2">
                <Chip active={selected.shape === 'rect'} onClick={() => patchSelected({ shape: 'rect' } as Partial<ZoneElement>)}>Téglalap</Chip>
                <Chip active={selected.shape === 'ellipse'} onClick={() => patchSelected({ shape: 'ellipse' } as Partial<ZoneElement>)}>Ellipszis</Chip>
              </div>
            </div>
            <div>
              <span className={label}>Szín</span>
              <div className="flex flex-wrap gap-2">
                {ZONE_COLORS.map((c) => (
                  <Swatch key={c} color={c} label="Szín" active={selected.color === c} onClick={() => patchSelected({ color: c })} />
                ))}
              </div>
            </div>
          </>
        )}

        {selected.type === 'text' && (
          <>
            <div>
              <label htmlFor="diagram-text" className={label}>Szöveg</label>
              <input
                id="diagram-text"
                type="text"
                maxLength={60}
                autoFocus
                value={selected.text}
                onFocus={(e) => {
                  snapshotRef.current = diagram;
                  e.target.select();
                }}
                onChange={(e) => preview((d) => updateElement(d, selected.id, (el) => ({ ...el, text: e.target.value })))}
                onBlur={() => commitFrom(snapshotRef.current)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label htmlFor="diagram-text-size" className={label}>Méret</label>
              <input
                id="diagram-text-size"
                type="range"
                min={14}
                max={64}
                value={selected.size}
                onPointerDown={() => (snapshotRef.current = diagram)}
                onChange={(e) => preview((d) => updateElement(d, selected.id, (el) => ({ ...el, size: Number(e.target.value) })))}
                onPointerUp={() => commitFrom(snapshotRef.current)}
                className="w-full"
              />
            </div>
            <div>
              <span className={label}>Szín</span>
              <div className="flex flex-wrap gap-2">
                {INK_COLORS.map((c) => (
                  <Swatch key={c} color={c} label="Szín" active={selected.color === c} onClick={() => patchSelected({ color: c })} />
                ))}
              </div>
            </div>
          </>
        )}

        <div className="flex gap-2 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={duplicateSelected}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            <DocumentDuplicateIcon className="h-4 w-4" />
            Duplikálás
          </button>
          <button
            type="button"
            onClick={deleteSelected}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-red-200 text-sm text-red-700 hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4" />
            Törlés
          </button>
        </div>
      </div>
    );
  };

  const iconButton =
    'flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent';

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      onKeyDown={handleRootKeyDown}
      className="fixed inset-0 z-[60] flex flex-col bg-gray-100 outline-none"
      role="dialog"
      aria-modal="true"
      aria-label="Rajzszerkesztő"
    >
      {/* Fejléc */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Rajzszerkesztő</p>
          <p className="truncate text-sm font-semibold text-gray-900">{title || 'Edzés ábra'}</p>
        </div>

        <select
          value={diagram.pitch}
          onChange={(e) => change((d) => ({ ...d, pitch: e.target.value as PitchType }))}
          aria-label="Pálya"
          className="rounded-md border border-gray-300 px-2 py-2 text-sm"
        >
          {PITCH_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <div className="flex items-center">
          <button type="button" onClick={undo} disabled={hist.past.length === 0} className={iconButton} aria-label="Visszavonás" title="Visszavonás (Ctrl+Z)">
            <ArrowUturnLeftIcon className="h-5 w-5" />
          </button>
          <button type="button" onClick={redo} disabled={hist.future.length === 0} className={iconButton} aria-label="Újra" title="Újra (Ctrl+Shift+Z)">
            <ArrowUturnRightIcon className="h-5 w-5" />
          </button>
          <button type="button" onClick={clearAll} disabled={diagram.elements.length === 0} className={iconButton} aria-label="Összes törlése" title="Összes elem törlése">
            <TrashIcon className="h-5 w-5" />
          </button>
          <button type="button" onClick={handleExport} disabled={exporting} className={iconButton} aria-label="Letöltés képként" title="Letöltés PNG képként">
            <ArrowDownTrayIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex gap-2">
          <button type="button" onClick={handleCancel} className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
            Mégse
          </button>
          <button type="button" onClick={handleSave} className="px-4 py-2 rounded-md bg-primary-600 text-sm font-semibold text-white hover:bg-primary-700">
            Kész
          </button>
        </div>
      </div>

      {/* Eszközök */}
      <div className="shrink-0 border-b border-gray-200 bg-white px-3 py-2">
        <div className="flex gap-1 overflow-x-auto">
          {TOOLS.map(({ tool: t, label }) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setTool(t);
                if (t !== 'select') setSelectedId(null);
              }}
              aria-pressed={tool === t}
              className={`flex shrink-0 flex-col items-center gap-0.5 rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                tool === t ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-200' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ToolIcon tool={t} />
              {label}
            </button>
          ))}
        </div>
        {renderToolOptions() && <div className="mt-2">{renderToolOptions()}</div>}
      </div>

      {/* Vászon + tulajdonságok */}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-2 md:p-4">
          <DiagramCanvas
            diagram={diagram}
            svgRef={svgRef}
            className="h-auto max-h-full w-full max-w-[1200px] rounded-lg shadow-lg select-none"
            onBackgroundPointerDown={handleCanvasPointerDown}
            onElementPointerDown={handleElementPointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            cursor={tool === 'select' ? 'default' : 'crosshair'}
            title={title}
            overlay={<g data-editor-overlay="true">{renderOverlay()}</g>}
          />
        </div>
        <aside className="shrink-0 overflow-y-auto border-t border-gray-200 bg-white p-4 lg:w-72 lg:border-l lg:border-t-0 max-h-[40vh] lg:max-h-none">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">
            {selected ? 'Kijelölt elem' : TOOLS.find((t) => t.tool === tool)?.label}
          </h3>
          {renderProperties()}
        </aside>
      </div>
    </div>
  );
};

export default DiagramEditor;
